import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, BookOpen, ChevronRight, Mail, Paperclip, Phone, Search, Send, X } from 'lucide-react';
import { supportApi, type SupportMessageDto, type SupportTicketDto } from '../../features/support/api';
import { CabinetHeading } from '../CabinetHeading/CabinetHeading';
import { MessageIcon } from '../NotificationBell/MessageIcon';
import { readStoredLanguage } from '../../i18n/locale';
import { useLanguage } from '../../i18n/LanguageProvider';
import { getRuntimeTimezone } from '../../i18n/preferences';

const CARRIER_FAQ = [
  ['Когда я получу оплату за перевозку?', 'Оплата поступает на внутренний баланс LOVO сразу после подтверждения заказчиком получения питомца.'],
  ['Как рассчитывается комиссия LOVO?', 'Комиссия составляет 5% от суммы перевозки и формируется после завершения заказа.'],
  ['Что делать, если заказчик отменил заявку?', 'Если заявка отменена после выезда, создайте обращение для разбора ситуации.'],
  ['Как повысить рейтинг фото?', 'Добавляйте фото на каждой контрольной точке перевозки.'],
  ['Что делать при споре с заказчиком?', 'Выберите тему «Спор»: мы рассмотрим заявку, переписку и фото контрольных точек.'],
] as const;
const CUSTOMER_FAQ = [
  ['Как выбрать перевозчика?', 'Сравните профиль, верификацию, портфолио и отзывы. Условия можно обсудить в приватном чате.'],
  ['Что делать, если возникла проблема в поездке?', 'Создайте обращение с темой «Спор» и опишите ситуацию.'],
  ['Где посмотреть статус перевозки?', 'Статус и контрольные точки доступны в разделе «Активные перевозки».'],
] as const;
const CARRIER_MATERIALS = ['Правила сервиса для перевозчиков', 'Как работает рейтинг и верификация', 'Договор оферты'] as const;
const CUSTOMER_MATERIALS = ['Правила сервиса для заказчиков', 'Как выбрать перевозчика', 'Безопасность перевозки'] as const;
const CATEGORIES = [
  ['request', 'Заявка'], ['payment', 'Оплата'], ['verification', 'Верификация'],
  ['dispute', 'Спор'], ['profile', 'Профиль'], ['technical', 'Техническая проблема'], ['other', 'Другое'],
] as const;

export interface SupportHelpViewProps {
  role: 'carrier' | 'customer';
  supportEmail?: string;
  supportPhone?: string;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: getRuntimeTimezone(),
  }).format(new Date(value));
}

export function SupportHelpView({ role, supportEmail = 'help@lovo.ru', supportPhone = '8 800 555-35-35' }: SupportHelpViewProps) {
  const { language } = useLanguage();
  const [query, setQuery] = useState('');
  const [tickets, setTickets] = useState<SupportTicketDto[]>([]);
  const [selected, setSelected] = useState<SupportTicketDto | null>(null);
  const [messages, setMessages] = useState<SupportMessageDto[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [category, setCategory] = useState('request');
  const [subject, setSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [reply, setReply] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const fallbackFaq = role === 'carrier' ? CARRIER_FAQ : CUSTOMER_FAQ;
  const [faqList, setFaqList] = useState<ReadonlyArray<readonly [string, string]>>(fallbackFaq);
  const materialsList = role === 'carrier' ? CARRIER_MATERIALS : CUSTOMER_MATERIALS;
  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru');
    return needle ? faqList.filter(([q, a]) => `${q} ${a}`.toLocaleLowerCase('ru').includes(needle)) : faqList;
  }, [faqList, query]);

  async function refreshTickets(selectId?: number) {
    const items = await supportApi.tickets();
    setTickets(items);
    if (selectId) setSelected(items.find((item) => item.id === selectId) ?? null);
  }

  useEffect(() => {
    refreshTickets().catch(() => setError('Не удалось загрузить обращения.')).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let active = true;
    setFaqList(fallbackFaq);
    supportApi.faq(role, language).then((items) => {
      if (active) setFaqList(items.map((item) => [item.question, item.answer] as const));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [fallbackFaq, language, role]);

  async function openTicket(ticket: SupportTicketDto) {
    setError(''); setSelected(ticket); setShowCreate(false);
    try {
      setMessages(await supportApi.messages(ticket.id));
      await refreshTickets(ticket.id);
    } catch { setError('Не удалось открыть переписку.'); }
  }

  async function uploadSelected() {
    if (!attachment) return null;
    setUploadProgress(0);
    const asset = await supportApi.uploadAttachment(attachment, setUploadProgress);
    setUploadProgress(null);
    return asset.id;
  }

  async function createTicket() {
    if (!subject.trim() || (!newBody.trim() && !attachment)) {
      setError('Укажите тему и опишите вопрос.'); return;
    }
    setSending(true); setError('');
    try {
      const assetId = await uploadSelected();
      const ticket = await supportApi.createTicket({
        category, subject: subject.trim(), initial_message: newBody.trim(), attachment: assetId,
      });
      setSubject(''); setNewBody(''); setAttachment(null); setShowCreate(false);
      await refreshTickets(ticket.id);
      await openTicket(ticket);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось создать обращение.');
    } finally { setSending(false); setUploadProgress(null); }
  }

  async function sendReply() {
    if (!selected || (!reply.trim() && !attachment)) return;
    setSending(true); setError('');
    try {
      const assetId = await uploadSelected();
      await supportApi.sendMessage(selected.id, reply.trim(), assetId);
      setReply(''); setAttachment(null);
      setMessages(await supportApi.messages(selected.id));
      await refreshTickets(selected.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось отправить сообщение.');
    } finally { setSending(false); setUploadProgress(null); }
  }

  return (
    <div className="carrier-help" data-state="ready" style={{ width: '100%' }}>
      <CabinetHeading className="carrier-help__top" figmaNode="2719:37086" title="Помощь" subtitle="Ответы на частые вопросы и связь со службой заботы LOVO" />
      <section className="carrier-help__card support-center" aria-label="Обращения в поддержку">
        <header>
          <div><h2>Поддержка</h2><p>Ваши обращения и ответы операторов LOVO</p></div>
          <button type="button" className="carrier-help__chat-button" onClick={() => { setShowCreate(true); setSelected(null); setError(''); }}><MessageIcon aria-hidden="true" hasUnread={tickets.some((item) => item.unread_count > 0)} size={20} /> Новое обращение</button>
        </header>
        {error ? <p className="support-center__error" role="alert">{error}</p> : null}
        {showCreate ? (
          <div className="support-center__compose">
            <div className="support-center__bar"><strong>Новое обращение</strong><button type="button" onClick={() => setShowCreate(false)} aria-label="Закрыть"><X /></button></div>
            <label><span>Тема вопроса</span><select value={category} onChange={(event) => setCategory(event.target.value)}>{CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label><span>Заголовок</span><input value={subject} maxLength={180} onChange={(event) => setSubject(event.target.value)} placeholder="Коротко опишите вопрос" /></label>
            <label><span>Сообщение</span><textarea value={newBody} onChange={(event) => setNewBody(event.target.value)} placeholder="Расскажите, что произошло" rows={5} /></label>
            <AttachmentPicker file={attachment} input={fileInput} onClear={() => setAttachment(null)} progress={uploadProgress} />
            <button type="button" className="support-center__submit" disabled={sending} onClick={() => void createTicket()}>{sending ? 'Отправляем…' : 'Отправить обращение'} <Send /></button>
          </div>
        ) : selected ? (
          <div className="support-center__dialog">
            <div className="support-center__bar"><button type="button" onClick={() => setSelected(null)}><ArrowLeft /> К обращениям</button><div><strong>{selected.public_code} · {selected.subject}</strong><span>{selected.status_label}</span><button type="button" className="support-center__status" onClick={async () => { const next = await supportApi.setStatus(selected.id, selected.status === 'closed' ? 'reopen' : 'close'); setSelected(next); await refreshTickets(next.id); }}>{selected.status === 'closed' ? 'Открыть снова' : 'Закрыть обращение'}</button></div></div>
            <div className="support-center__messages">{messages.map((message) => <article key={message.id} className={message.sender_role === 'support' ? 'is-support' : 'is-user'}><header><strong>{message.sender_name}</strong><time>{formatDate(message.created_at)}</time></header>{message.body ? <p>{message.body}</p> : null}{message.attachment_file ? <a href={message.attachment_file.download_url ?? message.attachment_file.url} target="_blank" rel="noreferrer"><Paperclip /> {message.attachment_file.original_filename || 'Вложение'}</a> : null}</article>)}</div>
            {selected.status !== 'closed' ? <div className="support-center__reply"><textarea value={reply} onChange={(event) => setReply(event.target.value)} rows={3} placeholder="Напишите сообщение поддержке" /><button type="button" className="support-center__clip" onClick={() => fileInput.current?.click()} aria-label="Прикрепить файл"><Paperclip /></button><button type="button" className="support-center__send" disabled={sending} onClick={() => void sendReply()} aria-label="Отправить"><Send /></button></div> : null}
            {attachment ? <small className="support-center__file">{attachment.name} <button type="button" onClick={() => setAttachment(null)}>Удалить</button></small> : null}
          </div>
        ) : (
          <div className="support-center__tickets">{loading ? <p>Загружаем обращения…</p> : tickets.length ? tickets.map((ticket) => <button type="button" key={ticket.id} onClick={() => void openTicket(ticket)}><span><strong>{ticket.public_code} · {ticket.subject}</strong><small>{ticket.category_label} · {ticket.status_label} · {formatDate(ticket.updated_at)}</small></span>{ticket.unread_count ? <b>{ticket.unread_count}</b> : <ChevronRight />}</button>) : <p>Обращений пока нет. Создайте первое, если понадобится помощь.</p>}</div>
        )}
        <input ref={fileInput} type="file" hidden accept="image/jpeg,image/png,image/webp,application/pdf,.docx,.xlsx,video/mp4,video/quicktime,video/webm" onChange={(event) => setAttachment(event.target.files?.[0] ?? null)} />
      </section>
      <label className="carrier-help__search"><Search aria-hidden="true" size={24} /><input value={query} placeholder="Найти ответ в справке..." onChange={(event) => setQuery(event.target.value)} /></label>
      <section className="carrier-help__card carrier-help__faq" data-figma-node="2719:37092"><h2>Частые вопросы</h2><div>{rows.map(([question, answer]) => <article key={question}><h3>{question}</h3><p>{answer}</p></article>)}</div></section>
      <section className="carrier-help__card carrier-help__contact" data-figma-node="2719:37110"><h2>Другие способы связи</h2><p>Наша поддержка на связи ежедневно с 8:00 до 22:00 по московскому времени</p><div><a className="carrier-help__contact-link" href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}><Phone aria-hidden="true" size={18} /> {supportPhone}</a><a className="carrier-help__contact-link" href={`mailto:${supportEmail}`}><Mail aria-hidden="true" size={18} /> {supportEmail}</a></div></section>
      <section className="carrier-help__card carrier-help__materials" data-figma-node="2719:37124"><h2>Полезные материалы</h2><div>{materialsList.map((title) => <button key={title} type="button"><span><BookOpen aria-hidden="true" size={20} /><span className="carrier-help__material-title">{title}</span></span><ChevronRight aria-hidden="true" size={18} /></button>)}</div></section>
    </div>
  );
}

function AttachmentPicker({ file, input, onClear, progress }: { file: File | null; input: React.RefObject<HTMLInputElement | null>; onClear: () => void; progress: number | null }) {
  return <div className="support-center__attachment"><button type="button" onClick={() => input.current?.click()}><Paperclip /> {file ? file.name : 'Прикрепить файл'}</button>{file ? <button type="button" onClick={onClear} aria-label="Удалить вложение"><X /></button> : null}{progress !== null ? <small>Загрузка: {progress}%</small> : null}</div>;
}
